import React, { useEffect, useState } from "react";

import { useHistory } from "react-router-dom";

import {
  YardiConnectionModal,
  YardiCredentialEditWarningModal,
} from "manager/components/Modal";
import ROUTES from "manager/config/routes";
import {
  useGetYardiCredentials,
  useDeleteYardiCredentials,
  useCreateYardiCredentials,
  useGetYardiPlatformTypes,
  usePollYardiCredentials,
  useUpdateYardiCredentials,
  useGetYardiVersion,
  useGetIntegrationStatuses,
  useTestConnection,
} from "manager/hooks/api";
import { IntegrationProvider } from "shared/config/constants";
import { useActiveCompany } from "shared/hooks";
import useModal from "shared/hooks/useModal";
import { BackLinkProps } from "shared/interfaces/misc";
import { transformRoute } from "shared/utils/routing";

import {
  getSelectedIntegrationProvider,
  useGetIntegrationProvider,
} from "../../config";
import { PollIntervalMilli, StatusesToPoll } from "../../IntegrationStatus";

import IntegrationLogin from "./IntegrationLogin";

interface IntegrationLoginContainerProps {
  backLink: BackLinkProps;
  editMode: boolean;
}

const IntegrationLoginContainer = ({
  backLink,
  editMode,
}: IntegrationLoginContainerProps) => {
  const history = useHistory();
  const { provider } = useGetIntegrationProvider();

  const { openModalDialog, closeActiveModalDialog } = useModal();
  const { activeCompany: company } = useActiveCompany();
  const { integrationStatuses } = useGetIntegrationStatuses();
  const selectedIntegrationStatus = getSelectedIntegrationProvider(
    integrationStatuses,
    provider
  );

  const { yardiPlatformTypes } = useGetYardiPlatformTypes();
  const { yardiVersion } = useGetYardiVersion(
    provider,
    editMode && provider === IntegrationProvider.yardi
  );
  let createOrUpdateYardiCredentials;
  let isSubmitLoading;
  let savedYardiCredentials;
  if (editMode) {
    const savedYardiCredentialsData = useGetYardiCredentials(provider);
    savedYardiCredentials = savedYardiCredentialsData.yardiCredentials;
    const updateYardiCredentialsData = useUpdateYardiCredentials({
      provider,
      companyId: company?.id,
    });
    createOrUpdateYardiCredentials =
      updateYardiCredentialsData.updateYardiCredentials;
    isSubmitLoading = updateYardiCredentialsData.isLoading;
  } else {
    const createYardiCredentialsData = useCreateYardiCredentials({
      provider,
      companyId: company?.id,
    });
    createOrUpdateYardiCredentials =
      createYardiCredentialsData.createYardiCredentials;
    isSubmitLoading = createYardiCredentialsData.isLoading;
  }

  const { deleteYardiCredentials } = useDeleteYardiCredentials(provider);

  const {
    yardiCredentials,
    yardiCredentialsError,
    isPollingActive,
    isPollYardiCredentialsError,
    startPolling,
    stopPolling,
  } = usePollYardiCredentials(provider, PollIntervalMilli);

  // Entrata test connection state
  const [connectionTested, setConnectionTested] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [errorTimeoutId, setErrorTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);

  const { testConnection, isTestingConnection } = useTestConnection();

  const openYardiConnectionModal = (data: {
    isLoading: boolean;
    cancelYardiConnectionOperation: () => void;
  }) => openModalDialog(YardiConnectionModal, { provider, ...data });

  const openYardiEditConfirmationModal = (data) =>
    openModalDialog(YardiCredentialEditWarningModal, data);

  const cancelYardiConnectionOperation = () => {
    stopPolling();
    const onSettled = () => closeActiveModalDialog();
    deleteYardiCredentials(null, { onSettled });
  };

  const submit = (values, setSubmitting) => {
    const onSuccess = () => {
      openYardiConnectionModal({
        isLoading: true,
        cancelYardiConnectionOperation,
      });
      startPolling();
    };
    const save = () => {
      createOrUpdateYardiCredentials(values, {
        onSuccess,
        onSettled: () => setSubmitting(false),
      });
    };
    if (editMode) {
      const onCancel = () => {
        closeActiveModalDialog();
        setSubmitting(false);
      };
      const onSubmit = () => {
        save();
      };
      openYardiEditConfirmationModal({ onCancel, onSubmit, provider });
    } else {
      save();
    }
  };

  const cancel = () => {
    history.push(
      transformRoute(ROUTES.integrationDashboard, {
        provider,
      })
    );
  };

  // Entrata test connection function
  const handleTestConnection = async (endpointUrl: string) => {
    if (provider !== IntegrationProvider.entrata) {
      return;
    }

    // Clear any existing timeout
    if (errorTimeoutId) {
      clearTimeout(errorTimeoutId);
      setErrorTimeoutId(null);
    }

    setConnectionTestResult(null);

    try {
      // Validate endpointUrl is provided
      if (!endpointUrl || endpointUrl.trim() === "") {
        setConnectionTestResult({
          success: false,
          message:
            "Please enter a valid Entrata URL before testing the connection.",
        });
        setConnectionTested(false);

        // Auto-hide error message after 4 seconds
        const timeoutId = setTimeout(() => {
          setConnectionTestResult(null);
        }, 3000);
        setErrorTimeoutId(timeoutId);
        return;
      }

      const result = await testConnection({
        provider: "entrata",
        endpointUrl,
      });

      setConnectionTestResult(result);

      if (result.success) {
        setConnectionTested(true);
      } else {
        setConnectionTested(false);
        // Auto-hide error message after 4 seconds
        const timeoutId = setTimeout(() => {
          setConnectionTestResult(null);
        }, 3000);
        setErrorTimeoutId(timeoutId);
      }
    } catch (error: any) {
      // Handle validation errors from serializer
      if (error?.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorMessages = Object.values(validationErrors).flat();
        setConnectionTestResult({
          success: false,
          message: `Validation error: ${errorMessages.join(", ")}`,
        });
      } else if (error?.response?.data?.message) {
        // Handle API error messages
        setConnectionTestResult({
          success: false,
          message: error.response.data.message,
        });
      } else {
        // Generic error fallback
        setConnectionTestResult({
          success: false,
          message: "Failed to test connection. Please try again.",
        });
      }
      setConnectionTested(false);

      // Auto-hide error message after 4 seconds
      const timeoutId = setTimeout(() => {
        setConnectionTestResult(null);
      }, 3000);
      setErrorTimeoutId(timeoutId);
    }
  };

  useEffect(() => {
    // NOTE: on the first login, we need to reload the company and wait for the response (isYardiConnected)
    // to be able to redirect to the Yardi Dashboard
    if (selectedIntegrationStatus?.isConnected) {
      const pullStatus = (yardiCredentials?.pullStatus || "").toLowerCase();
      if (isPollingActive && !StatusesToPoll.has(pullStatus)) {
        stopPolling();
        if (!isPollYardiCredentialsError) {
          openYardiConnectionModal({
            isLoading: false,
            cancelYardiConnectionOperation,
          });
        } else {
          // NOTE: if there is some error, means that the polling failed and we should just close the modal
          closeActiveModalDialog();
        }
      }
    }
  }, [
    yardiCredentials,
    yardiCredentialsError,
    selectedIntegrationStatus?.isConnected,
  ]);

  useEffect(() => closeActiveModalDialog, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutId) {
        clearTimeout(errorTimeoutId);
      }
    };
  }, [errorTimeoutId]);

  const updatedProps = {
    provider,
    backLink,
    disabled: selectedIntegrationStatus?.isSuspended,
    editMode,
    savedYardiCredentials,
    yardiPlatformTypes,
    isSubmitLoading,
    yardiVersion,
    submit,
    cancel,
    connectionTested,
    onTestConnection: handleTestConnection,
    isTestingConnection,
    connectionTestResult,
  };
  return <IntegrationLogin {...updatedProps} />;
};

export default IntegrationLoginContainer;
